import { useEffect, useState } from "react";
import { licenseAPI, customerAPI } from "../services/api";
import {
  VpnKey as LicenseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

export default function Dashboard() {
  const [licenses, setLicenses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    activated: 0,
    pending: 0,
    revoked: 0,
    expired: 0,
  });
  const [customerStats, setCustomerStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const formatDateShort = (date) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[date.getMonth()]} ${date.getDate()}`;
  };

  const loadData = async () => {
    try {
      const [licenseData, customerResponse] = await Promise.all([
        licenseAPI.getAll(),
        customerAPI.getAll(1, 1000), // Get a large number to get all customers for stats
      ]);
      
      setLicenses(licenseData);
      setCustomers(customerResponse.customers || []);
      
      const licenseStats = {
        total: licenseData.length,
        activated: licenseData.filter((l) => l.status === "activated").length,
        pending: licenseData.filter((l) => l.status === "pending").length,
        revoked: licenseData.filter((l) => l.status === "revoked").length,
        expired: licenseData.filter((l) => {
          if (!l.expiresAt) return false;
          return new Date(l.expiresAt) < new Date();
        }).length,
      };
      setStats(licenseStats);

      const customerStatsData = {
        total: customerResponse.total || 0,
        active: (customerResponse.customers || []).filter((c) => c.activeLicenseCount > 0).length,
        inactive: (customerResponse.customers || []).filter((c) => c.activeLicenseCount === 0).length,
      };
      setCustomerStats(customerStatsData);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const getStatusStats = () => {
    const stats = {
      activated: licenses.filter((l) => l.status === "activated").length,
      pending: licenses.filter((l) => l.status === "pending").length,
      revoked: licenses.filter((l) => l.status === "revoked").length,
      expired: licenses.filter((l) => {
        if (!l.expiresAt) return false;
        return new Date(l.expiresAt) < new Date();
      }).length,
    };
    return [
      { name: "Activated", value: stats.activated, color: "#10b981" },
      { name: "Pending", value: stats.pending, color: "#f59e0b" },
      { name: "Revoked", value: stats.revoked, color: "#ef4444" },
      { name: "Expired", value: stats.expired, color: "#94a3b8" },
    ];
  };

  const getTierStats = () => {
    const tiers = [
      { value: "basic", label: "Basic" },
      { value: "whatsapp", label: "Whatsapp" },
      { value: "ai_only", label: "AI Only" },
      { value: "ai_and_whatsapp", label: "AI and Whatsapp" },
    ];
    return tiers.map((tier) => ({
      name: tier.label,
      count: licenses.filter((l) => l.tier === tier.value).length,
    }));
  };

  const getActivationTimeline = () => {
    const last30Days = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = formatDateShort(date);
      const count = licenses.filter(
        (l) =>
          l.activatedAt && formatDateShort(new Date(l.activatedAt)) === dateStr
      ).length;
      last30Days.push({ date: dateStr, activations: count });
    }
    return last30Days;
  };

  const statCards = [
    {
      title: "Total Customers",
      value: customerStats.total,
      icon: PeopleIcon,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
    },
    {
      title: "Active Customers",
      value: customerStats.active,
      icon: CheckCircleIcon,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
    },
    {
      title: "Total Licenses",
      value: stats.total,
      icon: LicenseIcon,
      color: "text-slate-700",
      bgColor: "bg-slate-50",
    },
    {
      title: "Active Licenses",
      value: stats.activated,
      icon: CheckCircleIcon,
      color: "text-green-600",
      bgColor: "bg-green-50",
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
          <div className="text-slate-600 text-sm">
            Loading monitoring data...
          </div>
        </div>
      </div>
    );
  }

  const statusData = getStatusStats();
  const tierData = getTierStats();
  const timelineData = getActivationTimeline();

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 text-sm mt-1">
          Real-time license activity and analytics
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-slate-200 p-5 hover:border-slate-300 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-slate-600 text-sm mb-2">{card.title}</p>
                <p className="text-3xl font-semibold text-slate-900">
                  {card.value}
                </p>
              </div>
              <div className={`${card.bgColor} p-2.5 rounded-lg`}>
                <card.icon className={`w-5 h-5 ${card.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">
              Status Distribution
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              License breakdown by status
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={statusData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={90}
                fill="#8884d8"
                dataKey="value"
              >
                {statusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
            </PieChart>
          </ResponsiveContainer>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-200">
            {statusData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color }}
                ></div>
                <span className="text-sm text-slate-700">
                  {item.name}:{" "}
                  <span className="font-semibold">{item.value}</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* License Tiers */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-slate-900">
              License Tiers
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Distribution across tiers
            </p>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={tierData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12, fill: "#64748b" }}
                stroke="#cbd5e1"
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#64748b" }}
                stroke="#cbd5e1"
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: "14px",
                }}
              />
              <Bar dataKey="count" fill="#475569" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Activation Timeline */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-slate-900">
            Activation Timeline
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            License activations over the last 30 days
          </p>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={timelineData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: "#64748b" }}
              stroke="#cbd5e1"
            />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} stroke="#cbd5e1" />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
              }}
            />
            <Bar dataKey="activations" fill="#10b981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
